// First Goal — types + shaping (Phase 3, half-donut type).
//
// One half-donut per fixture team. Two view modes (a legacy `.view-toggle`):
//   - "Outcome"   — W/D/L split of games *after* the team scores/concedes first
//                   (which is chosen by the `.split-toggle`: SF = scores first,
//                   CF = concedes first). Green/grey/red arcs + a W/D/L stats row.
//   - "Frequency" — a single arc = the % of games the team scores (SF) or
//                   concedes (CF) first, with the centre % text shown.
// Pure + framework-free; the arc geometry + D3 animation live in <HalfDonut>.

import type { DonutSegment } from "./donut.js";

/** W/D/L percentages for one split + window. */
export interface FirstGoalOutcome {
  W: number;
  D: number;
  L: number;
}

/** How often the team scores-first (SF) / concedes-first (CF), for one window. */
export interface FirstGoalFreq {
  SF: number;
  CF: number;
}

type Window = "5" | "10" | "Season";

/** One team's First Goal data: outcomes per split + the scores/concedes-first
 *  frequencies, each keyed by window. */
export interface FirstGoalTeam {
  SF: Record<Window, FirstGoalOutcome>;
  CF: Record<Window, FirstGoalOutcome>;
  freq: Record<Window, FirstGoalFreq>;
}

/** First Goal data keyed by full team name. */
export type FirstGoalData = Record<string, FirstGoalTeam>;

export type FirstGoalSplit = "SF" | "CF";
export type FirstGoalView = "Outcome" | "Frequency";

/** A shaped donut: its arcs, optional centre % (Frequency), optional W/D/L
 *  stats row (Outcome). Matches the legacy renderer's per-donut output. */
export interface FirstGoalDonut {
  segments: DonutSegment[];
  /** Centre percentage text — shown in Frequency mode, null in Outcome mode. */
  pct: { value: number; color: string } | null;
  /** The `.fg-stats` W/D/L spans — shown in Outcome mode, null in Frequency. */
  statsSpans: { cls: string; text: string }[] | null;
}

/** Normalise the timeline control to a data key (anything but 10/Season → 5). */
function toWindow(timeline: string | null | undefined): Window {
  return timeline === "10" ? "10" : timeline === "Season" ? "Season" : "5";
}

// Segment colours, matching the legacy renderer exactly.
const GREEN = "#2ecc71";
const GREY = "#888";
const RED = "#e74c3c";

/**
 * Shape one team's First Goal donut for the current split/timeline/view.
 * `team` may be undefined (missing data) — falls back to the legacy defaults.
 */
export function shapeFirstGoalDonut(
  team: FirstGoalTeam | undefined,
  split: FirstGoalSplit,
  timeline: string | null | undefined,
  view: FirstGoalView
): FirstGoalDonut {
  const key: FirstGoalSplit = split === "CF" ? "CF" : "SF";
  const win = toWindow(timeline);

  if (view === "Frequency") {
    const f = team?.freq?.[win] ?? { SF: 0, CF: 0 };
    const pct = key === "CF" ? f.CF : f.SF;
    const color = key === "CF" ? RED : GREEN;
    return {
      segments: [
        { value: pct, color, key: "main" },
        { value: 100 - pct, color: GREY, key: "rest" },
      ],
      pct: { value: pct, color },
      statsSpans: null,
    };
  }

  // Outcome mode. The legacy renderer uses two different fallbacks for missing
  // data: the arcs default to L=100 (a full red donut) but the stats row shows
  // 0/0/0 — kept verbatim so the migration stays byte-identical.
  const raw = team?.[key]?.[win];
  const dArc = raw ?? { W: 0, D: 0, L: 100 };
  const dStat = raw ?? { W: 0, D: 0, L: 0 };
  return {
    segments: [
      { value: dArc.W, color: GREEN, key: "W" },
      { value: dArc.D, color: GREY, key: "D" },
      { value: dArc.L, color: RED, key: "L" },
    ],
    pct: null,
    statsSpans: [
      { cls: "fg-w", text: `${dStat.W}%` },
      { cls: "fg-d", text: `${dStat.D}%` },
      { cls: "fg-l", text: `${dStat.L}%` },
    ],
  };
}
